import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from 'lucide-react'
import styled from 'styled-components'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT, BORDER_RADIUS } from '../../../styles/theme'
import type { BatchQuestion, NodeMetadataEntry } from '../hooks/useBatchQuestionState'

interface CoveragePanelProps {
  questions: BatchQuestion[]
  metadataByNode: Record<string, NodeMetadataEntry>
}

interface NodeCoverage {
  nodeId: string
  label: string
  category: string
  total: number
  shared: number
}

const Container = styled.section`
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  background: var(--color-bg-subtle, rgba(0, 0, 0, 0.02));
  margin-bottom: ${SPACING.lg};
  overflow: hidden;
`

const SummaryRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  padding: ${SPACING.sm} ${SPACING.md};
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: var(--color-text-primary);
  transition: background 0.12s ease;

  &:hover {
    background: var(--color-bg-secondary);
  }
`

const SummaryIcon = styled.span<{ $ok: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${(p) => (p.$ok ? '#16a34a' : '#d97706')};
  flex-shrink: 0;
`

const SummaryText = styled.span`
  flex: 1;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  font-weight: ${FONT_WEIGHT.medium};
`

const ChevronWrap = styled.span`
  color: var(--color-text-tertiary);
  display: inline-flex;
  flex-shrink: 0;
`

const Details = styled.div`
  padding: ${SPACING.xs} ${SPACING.md} ${SPACING.md} ${SPACING.md};
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  padding: 4px 0;

  &:not(:last-child) {
    border-bottom: 1px dashed var(--color-border);
  }
`

const RowIcon = styled.span<{ $ok: boolean }>`
  color: ${(p) => (p.$ok ? '#16a34a' : '#d97706')};
  display: inline-flex;
  flex-shrink: 0;
`

const RowLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-text-primary);
`

const RowCategory = styled.span`
  text-transform: uppercase;
  font-size: 10px;
  font-weight: ${FONT_WEIGHT.semibold};
  letter-spacing: 0.4px;
  color: var(--color-text-tertiary);
  background: var(--color-bg-secondary);
  padding: 1px 6px;
  border-radius: ${BORDER_RADIUS.sm};
  flex-shrink: 0;
`

const RowCount = styled.span`
  font-variant-numeric: tabular-nums;
  color: var(--color-text-tertiary);
  flex-shrink: 0;
  font-size: ${TYPOGRAPHY.caption.fontSize};
`

function readSourceIds(q: BatchQuestion): string[] {
  if (q.sourceNodeIds && q.sourceNodeIds.length > 0) return q.sourceNodeIds
  if (q.sourceNodeId) return [q.sourceNodeId]
  return []
}

/**
 * Read-only audit shown above the question list.
 *
 * Tells the reviewer at a glance whether every selected node is being tested,
 * how many questions cover each, and which of those questions are shared.
 * Deliberately rendered as information (collapsible, dismissable mental model)
 * rather than as a structural divider — the AI annotates, the user owns the list.
 */
export function CoveragePanel({ questions, metadataByNode }: CoveragePanelProps) {
  const [open, setOpen] = useState(false)

  const { rows, uncoveredCount, sharedCount, unattributedCount } = useMemo(() => {
    const totals = new Map<string, { total: number; shared: number }>()
    let unattributed = 0
    let shared = 0
    for (const q of questions) {
      const ids = readSourceIds(q)
      if (ids.length === 0) {
        unattributed++
        continue
      }
      if (ids.length > 1) shared++
      for (const id of ids) {
        const entry = totals.get(id) ?? { total: 0, shared: 0 }
        entry.total++
        if (ids.length > 1) entry.shared++
        totals.set(id, entry)
      }
    }
    const rows: NodeCoverage[] = Object.entries(metadataByNode).map(([nodeId, meta]) => {
      const t = totals.get(nodeId) ?? { total: 0, shared: 0 }
      return {
        nodeId,
        label: meta.nodeLabel,
        category: meta.nodeCategory,
        total: t.total,
        shared: t.shared,
      }
    })
    rows.sort((a, b) => {
      if (a.total === 0 && b.total > 0) return -1
      if (b.total === 0 && a.total > 0) return 1
      return a.category.localeCompare(b.category) || a.label.localeCompare(b.label)
    })
    const uncoveredCount = rows.filter((r) => r.total === 0).length
    return { rows, uncoveredCount, sharedCount: shared, unattributedCount: unattributed }
  }, [questions, metadataByNode])

  const totalNodes = rows.length
  const ok = uncoveredCount === 0
  const summary = ok
    ? `All ${totalNodes} selected ${totalNodes === 1 ? 'node is' : 'nodes are'} covered · ${questions.length} ${questions.length === 1 ? 'question' : 'questions'}${sharedCount > 0 ? ` · ${sharedCount} shared` : ''}${unattributedCount > 0 ? ` · ${unattributedCount} unattributed` : ''}`
    : `${uncoveredCount} of ${totalNodes} ${uncoveredCount === 1 ? 'node has' : 'nodes have'} no questions yet · ${questions.length} ${questions.length === 1 ? 'question' : 'questions'} total`

  return (
    <Container>
      <SummaryRow type="button" onClick={() => setOpen((v) => !v)}>
        <SummaryIcon $ok={ok}>
          {ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
        </SummaryIcon>
        <SummaryText>{summary}</SummaryText>
        <ChevronWrap>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</ChevronWrap>
      </SummaryRow>
      {open && (
        <Details>
          {rows.map((r) => (
            <Row key={r.nodeId} title={r.label}>
              <RowIcon $ok={r.total > 0}>
                {r.total > 0 ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
              </RowIcon>
              <RowCategory>{r.category}</RowCategory>
              <RowLabel>{r.label}</RowLabel>
              <RowCount>
                {r.total === 0
                  ? 'no questions'
                  : `${r.total} ${r.total === 1 ? 'question' : 'questions'}${r.shared > 0 ? ` · ${r.shared} shared` : ''}`}
              </RowCount>
            </Row>
          ))}
        </Details>
      )}
    </Container>
  )
}
